import { type FormEvent, useEffect, useState } from 'react';
import { getAllIntegrationAccountEnvironments, getCurrentTenant } from '../../../sources/tenant';
import translations from '../../../translations';
import type { Environment, EnvironmentIntegrationAccount } from '../../../types/environment';
import type { IntegrationAccountEnvironments, TenantResponseAPI } from '../../../types/Tenant';
import Table from '../../generic/Table';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';

interface Props {
    environment: Environment | null;
    updateEnvironment: (environment: Environment) => void;
}

const IntegrationEnvironments = ({ environment, updateEnvironment }: Props) => {
    const [tenant, setTenant] = useState<TenantResponseAPI | null>(null);
    const [environments, setEnvironments] = useState<IntegrationAccountEnvironments | null>(null);
    const [loading, setLoading] = useState(true);

    const dispatchNotification = useNotificationDispatch();

    const loadIntegrationEnvironments = async () => {
        if (environment) {
            setLoading(true);

            try {
                setTenant(await getCurrentTenant());
                setEnvironments(await getAllIntegrationAccountEnvironments());
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: {
                        error,
                    },
                });
            } finally {
                setLoading(false);
            }
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to add all dependencies
    useEffect(() => {
        loadIntegrationEnvironments();
    }, [environment?.id]);

    if (!(environment && tenant)) {
        return null;
    }

    const onIntegrationEnvironmentChange = (accountId: string, environmentId: string) => {
        let integrationAccounts = environment.integrationAccounts;

        if (!integrationAccounts) {
            integrationAccounts = [];
        }

        if (environment.integrationAccounts.find((account) => account.accountId === accountId)) {
            integrationAccounts = environment.integrationAccounts.map((account) => {
                if (account.accountId === accountId) {
                    return { accountId, environmentId };
                }

                return account;
            });
        } else {
            integrationAccounts.push({ accountId, environmentId });
        }

        updateEnvironment({
            ...environment,
            integrationAccounts,
        });
    };

    const accounts: EnvironmentIntegrationAccount[] =
        tenant?.tenantSettings?.integrationAccounts?.map((account) => {
            return {
                accountId: account.accountId,
                environmentId:
                    environment.integrationAccounts?.find(
                        (environmentAccount) => environmentAccount.accountId === account.accountId,
                    )?.environmentId || '',
            };
        }) ?? [];

    const columns = [
        {
            renderHeader: () => translations.TENANT_integration_accounts_account_id as string,
            renderCell: ({ item }: { item: EnvironmentIntegrationAccount }) => item.accountId,
        },
        {
            renderHeader: () => translations.ENVIRONMENT_integration_account_environment,
            renderCell: ({ item }: { item: EnvironmentIntegrationAccount }) => {
                return (
                    <select
                        className="form-control"
                        disabled={loading}
                        value={item.environmentId}
                        onChange={(e: FormEvent<HTMLSelectElement>) =>
                            onIntegrationEnvironmentChange(item.accountId, e.currentTarget.value)
                        }
                        aria-label={`${item.accountId} Environments`}
                    >
                        <option>
                            {translations.ENVIRONMENT_select_integration_account_environment}
                        </option>
                        {environments?.[item.accountId]?.map((env) => (
                            <option value={env.id} key={env.id}>
                                {env.name}
                            </option>
                        ))}
                    </select>
                );
            },
        },
    ];

    return (
        <Table<EnvironmentIntegrationAccount>
            tableClass="generic-table margin-top"
            items={accounts || []}
            columns={columns}
            isLoading={loading}
        />
    );
};

export default IntegrationEnvironments;
